import { useCurrentCountry, useInstagramWeeks } from 'utils/hooks';
import useInstagramData, {
    useInstagramHashtagsByName,
} from 'utils/hooks/useInstagramData';
import { DATA_SOURCES } from '@/config/constants';
import { isDataSupported } from '../utils';
import { useInstagramWatchlistRelatedCategoryData } from 'utils/hooks/api';

export const useInstagramWatchlistData = ({
    brandIds,
    categoryIds,
    instagramHashtags,
    relatedCategoryParentCategoryItems = [],
    relatedCategoryParentBrandItems = [],
    categoryPairs = [],
    brandPairs = [],
    defaultInitialSortOption,
    placeholderDataByType,
}) => {
    const country = useCurrentCountry();
    const { week, year } = useInstagramWeeks();
    // Fetch Instagram data for brands
    const instagramBrandsDataQuery = useInstagramData({
        paginateBy: 'brands',
        brands: brandIds,
        countries: country ? [country.id] : null,
        enabled: !!(week && year && country && brandIds?.length),
        order_by: defaultInitialSortOption?.sortBy,
        ascending: defaultInitialSortOption?.ascending,
        placeholderData: placeholderDataByType?.brand?.map((e) => e) || [],
    });

    // Fetch Instagram data for categories
    const instagramCategoriesDataQuery = useInstagramData({
        paginateBy: 'categories',
        categories: categoryIds,
        countries: country ? [country.id] : null,
        enabled: !!(week && year && country && categoryIds?.length),
        order_by: defaultInitialSortOption?.sortBy,
        ascending: defaultInitialSortOption?.ascending,
        placeholderData: placeholderDataByType?.category?.map((e) => e) || [],
    });

    // Extract hashtag names from instagramHashtags for fetching
    const hashtagNames =
        instagramHashtags?.map((hashtag) => hashtag.name) || [];

    // Fetch Instagram data for hashtags using the new watchlist endpoint
    const instagramHashtagsDataQuery = useInstagramHashtagsByName({
        hashtags: hashtagNames?.length ? hashtagNames : null,
        countries: country ? [country.id] : null,
        enabled: !!(week && year && country && hashtagNames?.length),
        order_by: defaultInitialSortOption?.sortBy,
        ascending: defaultInitialSortOption?.ascending,
        placeholderData: placeholderDataByType?.hashtag?.map((e) => e) || [],
    });

    // Keyword-matching cutover: pair endpoint fetches (parent, related_category)
    // matches from the IG aggregate tables in one round trip. Mirrors the TT
    // watchlist enrichment flow.
    const relatedCategoryPairsQuery = useInstagramWatchlistRelatedCategoryData({
        categoryPairs,
        brandPairs,
        countries: country ? [country.id] : null,
        week,
        year,
        enabled: !!country,
        placeholderData: placeholderDataByType?.relatedCategory?.map((e) => e),
    });

    const allRelatedCategories = (
        relatedCategoryPairsQuery.data?.data || []
    ).map((row) => {
        // Prefer the parent name freshly resolved by the BE pair endpoint
        // (joins the parent Category/Brand table on every fetch). Falls back
        // to the snapshot stored on the watchlist item when present, then to
        // the joined brand name for legacy cases.
        const savedParentName = row.categoryId
            ? relatedCategoryParentCategoryItems.find(
                  ({ categoryId, relatedCategoryId }) =>
                      categoryId === row.categoryId &&
                      relatedCategoryId === row.relatedCategoryId,
              )?.parentName
            : relatedCategoryParentBrandItems.find(
                  ({ brandId, relatedCategoryId }) =>
                      brandId === row.brandId &&
                      relatedCategoryId === row.relatedCategoryId,
              )?.parentName;
        const parentName =
            row.parent_name || savedParentName || row.brandName || '';
        return {
            ...row,
            type: 'related-category',
            relatedType: row.related_type || 'category',
            relatedName: parentName,
            exportName: parentName ? `${parentName} - ${row.name}` : row.name,
        };
    });

    // Combine all Instagram data and filter for supported data
    const allInstagramData = [
        ...(instagramBrandsDataQuery.data || []),
        ...(instagramCategoriesDataQuery.data || []),
        ...(instagramHashtagsDataQuery.data?.map((d) => ({
            ...d,
            type: 'instagram-hashtag',
        })) || []),
        ...allRelatedCategories,
    ].filter((d) => isDataSupported(d, country, DATA_SOURCES.INSTAGRAM));

    // Check if data is loading
    const isLoading =
        instagramBrandsDataQuery.isLoading ||
        instagramCategoriesDataQuery.isLoading ||
        instagramHashtagsDataQuery.isLoading ||
        relatedCategoryPairsQuery.isLoading ||
        (brandIds && !instagramBrandsDataQuery.firstPageQuery.isFetched) ||
        (categoryIds &&
            !instagramCategoriesDataQuery.firstPageQuery.isFetched) ||
        (hashtagNames?.length &&
            !instagramHashtagsDataQuery.firstPageQuery.isFetched);
    const isPlaceholderDataLoaded =
        (categoryIds && instagramCategoriesDataQuery.isPlaceholderData) ||
        (brandIds && instagramBrandsDataQuery.isPlaceholderData) ||
        (hashtagNames?.length && instagramHashtagsDataQuery.isPlaceholderData);

    const isDataLoaded = !isLoading || isPlaceholderDataLoaded;

    return {
        instagramBrandsDataQuery,
        instagramCategoriesDataQuery,
        instagramHashtagsDataQuery,
        relatedCategoryPairsQuery,
        allRelatedCategories,
        allInstagramData,
        isDataLoaded,
        isPlaceholderDataLoaded,
    };
};
